import { useEffect, useMemo, useCallback } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useServices } from "@/services-context";
import styles from "./brand-page.module.scss";
import { FixLoader } from "@/elements-react/fix-loader";
import { LocationHook, RouteHook } from "preact-iso";
import FixBreadcrumb, { useBreadcrumbs } from "@/elements-react/fix-breadcrumb";
import { Url } from "@/helpers/url";
import { CategoriesPreviewCustom } from "@/elements-react/categories-preview-custom";
import ProductAreaElement from "@/elements-react/product-area-element";
import { B2cProductSearchPayload } from "@/generated/graphql/b2c";
import { BrandsListLinks } from "@/elements-react/brands-list-links";
import FixText from "@/elements-react/fix-text";
import { Services } from "@/services/services";
import { useLayoutType, LayoutStyleTypes, LayoutTypes } from "@/util-react/layout";
import { useProductAreaStore } from "@/elements-react/product-area/store/hook";
import { createLocationParams, getLocationValues } from "@/elements-react/product-area/store/location-utils";
import { AreaFilters, getHash } from "@/elements-react/product-area/store/area-filters";
import { ProductAreaContext } from "@/elements-react/product-area/context";
import { FORMATTER_LISTENER_NAME } from "@/services/formatter.ts";
import { CATEGORIES_LISTENER_NAME, MenuItem } from "@/services/categories.ts";
import FixBackButton from "@/elements-react/fix-back-button";
import { CatalogPageStore } from "../catalog-page-common/page-store";
import { PageTags } from "@/elements-react/document-elements/page-tags";
import { useRef } from "preact/hooks";

const getFeatureBrandsPosition = (type: LayoutStyleTypes.TYPE_SM | LayoutStyleTypes.TYPE_LG, navigator: { top: boolean; sub: boolean }) => {
  if (type === LayoutStyleTypes.TYPE_LG) {
    return "before-product-area" as const;
  }

  if (type === LayoutStyleTypes.TYPE_SM && navigator.top && navigator.sub) {
    return "after-product-area" as const;
  }

  if (type === LayoutStyleTypes.TYPE_SM && navigator.sub) {
    return "before-product-area" as const;
  }

  if (type === LayoutStyleTypes.TYPE_SM) {
    return "end-topcard" as const;
  }
};

const getView = (services: Services, root: string, type: LayoutStyleTypes.TYPE_SM | LayoutStyleTypes.TYPE_LG) => {
  const path = services.categories
    .getParents(root)
    .map((item) => item.id)
    .concat(root);
  const depth = path.length;
  const active = path[path.length - 1] ?? null;
  const children = active ? services.categories.getChilds(active) : [];
  const view = {
    categoryTabs: null as { parentId: string; activeId: string | null } | null,
    categoryCards: null as { parentId: string; activeId: string | null } | null,
    brands: "none" as string | undefined,
  };

  if (depth <= 1) {
    view.categoryTabs = { parentId: "*", activeId: active ?? null };
  }

  if (active && children.length) {
    view.categoryCards = { parentId: active, activeId: null };
  }

  view.brands = getFeatureBrandsPosition(type, {
    top: Boolean(view.categoryTabs?.parentId),
    sub: Boolean(view.categoryCards?.parentId),
  });

  return view;
};

export const BrandPage = ({ location, route, store }: { location: LocationHook, route: RouteHook, store: CatalogPageStore }) => {
  const { t } = useTranslation();
  const services = useServices([FORMATTER_LISTENER_NAME, CATEGORIES_LISTENER_NAME]);
  const filters = getLocationValues(location);
  const sendGoogleEventViewItemList = useRef<() => void | null>(null);
  const preloadedBrandsByCategory = services.preloaded.brandsByCategory;

  const updatePageData = useCallback(async (params: { route: RouteHook, location: LocationHook, filters: AreaFilters }) => {
    await store.loadForLocation({
      location: params.location,
      route: params.route,
      preloadedBrandsByCategory: preloadedBrandsByCategory,
    });
  }, [store]);

  const state = store.result();

  useEffect(() => {
    return () => {
      store.release();
    };
  }, [route.path]);

  useEffect(() => {
    updatePageData({
      route: route,
      location: location,
      filters: filters,
    });
  }, [route.path, location, getHash(filters)]);

  useEffect(() => {
    if (sendGoogleEventViewItemList.current && state.loadedProducts.length > 0) {
      sendGoogleEventViewItemList.current();
    }
  }, [JSON.stringify(state.loadedProducts)]);

  const products: B2cProductSearchPayload[] = useMemo(() => {
    return Object.values(state.groups).reduce((acc, group) => {
      return acc.concat(group.products);
    }, [] as B2cProductSearchPayload[]);
  }, [Object.keys(state.groups).join("-")]);

  const allBrands = services.brands.list;

  const categories = useMemo(() => {
    const categories = state.categoriesByBrand?.map((id) => services.categories.getItem(id)).filter((item) => item?.level === 1) ?? [];

    const result: MenuItem[] = [];

    categories.forEach((item) => {
      if (!item) {
        return;
      }

      const [parent] = services.categories.getParents(item.id);

      if (parent && !result.find((i) => i.id === parent!.id)) {
        result.push(parent);
      }
    });

    return result;
  }, [state.categoriesByBrand, services.categories]);
  const brands = useMemo(() => [], []);

  const {
    brand
  } = state;

  const seoMarkupDataset = useMemo(() => {
    return {
      type: 'brand',
      canonical: store.canonicalUrl,
      alternates: store.alternatesWithPage,
    } as const;
  }, [state.registryUrl, state.alternates, state.currentPage]);

  const breadcrumbs = useMemo(() => {
    const root = brand!.id;
    const path = [root];
    const breadcrumbs = path.map((id) => {
      const item = services.brands.getItem(id);
      const url = Url.to("dynamic-route", { dynamicPath: item.slug });
      return {
        label: item.name,
        url: url,
      };
    });
    return breadcrumbs;
  }, [brand!.id, services.brands]);

  useBreadcrumbs(breadcrumbs);

  const { layoutType, layoutStyleType, defaultLayoutStyleType } = useLayoutType();
  const type = defaultLayoutStyleType as LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM;
  const isDesktop = layoutType === LayoutTypes.TYPE_DESKTOP;

  const navigate = useCallback((params: { brand: string; params?: URLSearchParams, mode?: 'pushState' | 'replaceState' }) => {
    const path = services.brands.getItem(params.brand).slug;
    const searchParams = params.params ?? new URLSearchParams();
    const url = Url.to("dynamic-route", { dynamicPath: path }, searchParams.toString());
    const mode = params.mode ?? 'replaceState';
    location.route(url, mode === 'replaceState');
  }, []);

  const onFiltersChange = useCallback((filters: AreaFilters) => {
    const { search } = createLocationParams(filters);
    navigate({
      brand: brand!.id,
      params: search,
    });
  }, [brand, navigate]);

  const areaStore = useProductAreaStore({
    filters,
    brands,
    categories,
    products,
    more: state.more,
    onFiltersChange: onFiltersChange,
    totalItems: state.totalItems,
  });

  const view = useMemo(() => {
    return getView(services, brand!.id, type);
  }, [services, brand, type]);

  const renderNavigator = () => {
    const root = view.categoryCards?.parentId;
    const items = root ? services.categories.getChilds(root) : [];
    const categories = items.map((category) => ({
      name: category.name,
      key: category.id,
      id: category.id,
      image: category.image,
      url: category.url,
      level: category.level.toString(),
    }));

    return <CategoriesPreviewCustom type={type as LayoutStyleTypes.TYPE_LG | LayoutStyleTypes.TYPE_SM} categories={categories} />;
  };

  const renderPopularBrands = () => (
    <div className={classNames(styles.section, styles.card, styles["section-card-type-2"])}>
      <h2 className={styles["section-title"]}>{t("22462yo1x6yur1ek.car-brands-title", "Buy car parts from the most popular car brands")}</h2>
      <div className={styles["section-box"]}>
        <BrandsListLinks type={type} brands={allBrands} />
      </div>
    </div>
  );

  const renderAbout = () => {
    return (
      <div className={classNames(styles.section, styles.card, styles["type-1"])}>
        <div className={styles["section-box"]}>
          {
            brand?.description?.length > 0 && (
                  <FixText type={type} text={brand!.description} showMore={false} />
              )
          }
        </div>
      </div>
    );
  };

  const renderTopcard = () => (
    <div className={classNames(styles.section, styles.normargin, styles.card, styles["type-card-top"])}>
      <div className={classNames(styles.container)}>
        {/* TODO: Display alerts when needed (already exists in main layout?)
        <div className={classNames(styles.row, styles.alerts)}>
          <FixAlert />
        </div>
        */}

        <div className={classNames(styles.row, styles.navigation)}>
          {isDesktop && (
            <div>
              {/* TODO: Display back button before breadcrumb and fix it ckick event (back in history?) */}
              <FixBackButton className="back" mode="link" />
            </div>
          )}

          <FixBreadcrumb />
        </div>

        <div className={classNames(styles.row, styles.primary)}>
          <h1 className={styles["section-title"]}>{brand.name}</h1>
          {isDesktop && (
            <>
              <div className={classNames(styles.results)}>{t("j8cinnz091w154tp.results", "results")}</div>
            </>
          )}
        </div>

        <div>{renderNavigator()}</div>
      </div>
    </div>
  );

  const metaDataset = useMemo(() => {
    const brandName = brand!.name!;
    const currentPage = filters.page;

    const title = services.pagemeta.createBrandTitle({
      brandName,
      currentPage: currentPage > 1 ? currentPage : null,
    });
    const description = services.pagemeta.createBrandDescription({
      brandName,
    });

    return { title, description };
  }, [brand, filters.page]);


  return (
    <ProductAreaContext.Provider value={areaStore}>
      {state.loading && (<FixLoader />)}
      {!state.blank && (
        <div className={classNames(styles[type])}>
          {renderTopcard()}
          <ProductAreaElement type={type} resultState={state} pageType="brand" sendAction={sendGoogleEventViewItemList} />
          {renderPopularBrands()}
          {renderAbout()}
        </div>
      )}
      <PageTags meta={metaDataset} seo={seoMarkupDataset} />
    </ProductAreaContext.Provider>
  );
};

export default BrandPage;
